import type { KeyEstimate } from '@/core/analysis/types';
import type { Instrument } from '@/core/instruments/shapes';

/**
 * A remembered song. Only chords and metadata are stored (ADR 0005): the audio is never saved,
 * so after a reload the user selects the file again and its content hash finds this record.
 */
export interface Song {
  /** SHA-256 of the file contents (F2.13). */
  id: string;
  /** From tags (Phase 4), else the file name without extension. */
  title: string;
  artist?: string;
  /** Cover art thumbnail from the tags, as a small JPEG data URL. */
  cover?: string;
  fileName: string;
  fileSize: number;
  /** Set for songs from a YouTube link: played in the embedded video, not from a file. */
  youtubeId?: string;
  mimeType: string;
  duration: number;
  /** Copied from the analysis so the library list needs no extra reads. */
  summary: { key: KeyEstimate; bpm: number; chordChanges: number };
  createdAt: number;
  lastOpenedAt: number;
}

export type { Instrument };

/** Per-song memory (F5.7). */
export interface SongSettings {
  songId: string;
  transpose: number;
  capo: number;
  instrument: Instrument;
  speed: number;
  loop: { start: number; end: number } | null;
  /** Pitch-shift the recording to match the transpose (F3.3). Missing in older saves. */
  shiftAudio?: boolean;
}

/** App settings (F10.3). */
export interface AppSettings {
  theme: 'system' | 'light' | 'dark';
  defaultInstrument: Instrument;
  accidentals: 'auto' | 'sharps' | 'flats';
  notation: 'letters' | 'german' | 'solfege' | 'roman' | 'nashville';
  leftHanded: boolean;
  /** Chord grid text size, 0.8–1.4 (F4.13). */
  gridTextScale: number;
  barsPerLine: 2 | 4 | 6;
  /** Player view (F4.1 grid, F4.2 timeline, F4.3 stage, F4.5 fretboard/keyboard). */
  playerView: 'grid' | 'timeline' | 'stage' | 'neck';
  /** Timeline diagram strip: every chord change in order, or each chord once. */
  diagramStrip: 'animated' | 'summary';
  /** Visual sync calibration added to the audio clock. */
  visualOffsetMs: number;
  /** Chord detail (F2.11): triads only, common extensions, or everything detected. */
  complexity: 'simple' | 'standard' | 'advanced';
  /**
   * Chord recognition (ADR 0009): the BTC AI model where available (YouTube songs, on the
   * server), or this app's classic engine.
   */
  chordModel: 'btc' | 'classic';
  /** Practice sounds (F3.6–F3.8): on/off and volume 0–1. */
  metronome: boolean;
  metronomeVolume: number;
  countIn: boolean;
  synth: boolean;
  synthVolume: number;
  /** Flash the next chord this many beats before it starts (F4.7); 0 = off. */
  changeWarningBeats: 0 | 1 | 2 | 4;
  /** Easy mode (F5.4): plain triads and the easiest shapes. */
  easyMode: boolean;
  /** Reduce motion even if the system doesn't ask for it (F10.9). */
  reduceMotion: boolean;
  /** Playback volume 0–1 (F3.9), remembered between songs. */
  volume: number;
  /** Chosen voicing per chord shape (F4.9), keyed "instrument:root:quality" → index. */
  voicingChoices: Record<string, number>;
}

export const DEFAULT_APP_SETTINGS: AppSettings = {
  theme: 'system',
  defaultInstrument: 'guitar',
  accidentals: 'auto',
  notation: 'letters',
  leftHanded: false,
  gridTextScale: 1,
  barsPerLine: 4,
  playerView: 'timeline',
  diagramStrip: 'animated',
  visualOffsetMs: 0,
  complexity: 'simple',
  chordModel: 'btc',
  metronome: false,
  metronomeVolume: 0.6,
  countIn: false,
  synth: false,
  synthVolume: 0.6,
  changeWarningBeats: 2,
  easyMode: false,
  voicingChoices: {},
  volume: 1,
  reduceMotion: false,
};
